import React, { useCallback, useMemo } from 'react';
import Table from '../../components/Table';
import useLabels from './hooks/useLabels';
import usePaginatedItems from '../hooks/usePaginatedItems';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import { StoreInventoryProduct } from 'api/storeInventory';
import { useTranslation } from 'react-i18next';
import {
  storeProductInventoryByOptions,
  StoreProductInventorySearchBy,
} from 'types';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import { Link, useParams } from 'react-router-dom';

import './StoreInventory.styles.scss';

const StoreInventory = () => {
  const labels = useLabels();

  const { storeNo } = useParams();

  const { t } = useTranslation();

  const {
    setQueryParam,
    params: { page, search, limit, searchBy },
  } = useQueryParams<{
    page: string;
    search: string;
    limit: string;
    searchBy: StoreProductInventorySearchBy;
  }>({ searchBy: 'skuNo' });

  const { items, isFetching, totalItems, totalPages } = usePaginatedItems({
    queryKey: 'store-inventory-view',
    makeRequest: api.storeInventory.getStoreProductInventoryView,
    params: {
      limit,
      page,
      [searchBy]: !!search && search?.length >= 2 ? search : undefined,
      storeNo,
    },
  });

  useEffectSkipFirst(() => {
    page !== '1' && setQueryParam('page', '1', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, limit]);

  const renderContentArray = useCallback(
    (data: StoreInventoryProduct[]) =>
      data?.map(({ skuNo, ...rest }) => ({
        skuNo: (
          <Link
            className="sku-link"
            to="../inventory-locations"
            state={{ skuNo }}
          >
            {skuNo}
          </Link>
        ),
        ...rest,
      })),
    [],
  );

  const searchByOptions = useMemo(
    () =>
      Object.values(storeProductInventoryByOptions).map(({ value, label }) => ({
        label: t(`${label}`),
        value,
      })),
    [t],
  );

  const handleSearchByChange = useCallback(
    (option: OptionValue<any>) => {
      setQueryParam('searchBy', option.value, true);
    },
    [setQueryParam],
  );

  return (
    <Table
      title={t('InventoryCount')}
      labels={labels}
      fetching={isFetching}
      contentArray={renderContentArray(items)}
      totalItems={totalItems}
      className="inventory-count"
      totalPages={totalPages}
      elementProps={labels.map(({ key }) => key)}
      searchable
      searchBy={searchBy}
      searchByOptions={searchByOptions}
      searchPlaceholder={t('StoreProductInventory.searchPlaceholder')}
      onSearchByChange={handleSearchByChange}
    />
  );
};

export default StoreInventory;
